import toast from "react-hot-toast";
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/cloudflare";
import {
  json,
  redirect,
} from "@remix-run/cloudflare";
import {
  Form,
  useActionData,
  useNavigation,
} from "@remix-run/react";
import { parse } from "valibot";
import { ResetSchema } from "~/types/schema";
import { NOT_ALOOW_INPUT } from "~/utils/authUtils";
import { createSbClientSSR } from "~/utils/createSbClientSSR";
import { parseWithValibot as conformParse } from "conform-to-valibot";
import {
  getFormProps,
  getInputProps,
  useForm,
} from "@conform-to/react";
import ErrorDisplay from "~/components/Common/ErrorDisplay";
import clsx from "clsx";
import {
  buttonBase,
  inputBase,
} from "~/components/Common/FormStyle";
import { useEffect } from "react";
import {
  DEFAULT_DESCRIPTION,
  TITLE_SUFFIX,
} from "~/utils/const";

export const meta: MetaFunction = () => {
  return [
    {
      title: "パスワードを忘れた" + TITLE_SUFFIX,
    },
    {
      name: "description",
      content: DEFAULT_DESCRIPTION,
    },
  ];
};

// add the loader
export async function loader({
  request,
  context,
}: LoaderFunctionArgs) {
  const headers = new Headers();
  const supabase = createSbClientSSR(
    headers,
    request,
    context,
  );
  const userResponse =
    await supabase.auth.getSession();
  if (userResponse.data.session?.user.id) {
    return redirect(
      `/user/${userResponse.data.session?.user.id}`,
      {
        headers: headers,
      },
    );
  } else {
    return null;
  }
}

export async function action({
  request,
  context,
}: ActionFunctionArgs) {
  const headers = new Headers();
  const supabase = createSbClientSSR(
    headers,
    request,
    context,
  );
  const formData = await request.formData();
  const dataFields = Object.fromEntries(
    formData.entries(),
  );
  const submission = conformParse(formData, {
    schema: ResetSchema,
  });

  if (submission.status !== "success") {
    return json(
      {
        ok: false,
        error: {
          auth: NOT_ALOOW_INPUT,
          ...submission.error,
        },
        submission,
      },
      {
        headers: headers,
      },
    );
  }

  const parsed = parse(ResetSchema, dataFields);

  const url = new URL(request.url);
  const siteURLRoot = url.origin;
  const res =
    await supabase.auth.resetPasswordForEmail(
      parsed.email,
      {
        redirectTo:
          siteURLRoot + "/update-password",
      },
    );
  console.log("res:", res);
  if (res.error) {
    return json(
      {
        ok: false,
        error: {
          auth: res.error.message,
        },
      },
      {
        headers: headers,
      },
    );
  } else {
    return json(
      {
        ok: true,
        error: null,
      },
      {
        headers: headers,
      },
    );
  }
}

export default function Index() {
  const data = useActionData<typeof action>();

  useEffect(() => {
    if (data?.ok) {
      const isLocal =
        window.location.hostname === "localhost";
      toast.success(
        "リセットメールを送りました",
        {
          duration: 4000,
        },
      );
      if (isLocal) {
        window.open(
          "http://127.0.0.1:54324/monitor",
          "_blank",
        );
      }
    }
  }, [data?.ok]);

  const [form, { email }] = useForm({
    onValidate({ formData }) {
      const check = conformParse(formData, {
        schema: ResetSchema,
      });
      return check;
    },
  });
  const { state } = useNavigation();
  return (
    <div className={clsx("pt-96")}>
      {data?.error && (
        <ErrorDisplay
          error={data?.error}
        ></ErrorDisplay>
      )}
      <h1
        className={clsx(
          "font-bold",
          "text-lg",
          "text-center",
        )}
      >
        パスワードリセット
      </h1>
      <div
        className={clsx(
          "mx-auto max-w-contentMd",
          "mt-48",
        )}
      >
        <Form
          method="post"
          {...getFormProps(form)}
        >
          <div
            className={clsx(
              "flex",
              "flex-col",
              "gap-16",
            )}
          >
            <div className="">
              <input
                className={inputBase()}
                placeholder="email"
                {...getInputProps(email, {
                  type: "email",
                })}
              />
              <div className="mt-4">
                {email.errors && (
                  <ErrorDisplay
                    error={{
                      email: email.errors,
                    }}
                  ></ErrorDisplay>
                )}
              </div>
            </div>
          </div>

          <div
            className={clsx(
              "text-center",
              "mt-24",
            )}
          >
            <button
              className={buttonBase({
                loading:
                  state === "loading" ||
                  state === "submitting",
              })}
              type="submit"
            >
              リセット
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
}
